import { useState, useEffect, useCallback } from 'react'
import {
  getAdminUsers,
  createAdminUser,
  updateAdminUser,
  removeAdminUser,
  type AdminUserSummary,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission, useAdminUser } from '../../hooks/usePermission'
import { formatRole } from '../../utils/formatRole'

const roleColors: Record<string, string> = {
  super_admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  moderator: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  support: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
}

const roleOptions = ['support', 'moderator', 'admin', 'super_admin']

export function Settings() {
  const [users, setUsers] = useState<AdminUserSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newRole, setNewRole] = useState('support')
  const [editingUser, setEditingUser] = useState<AdminUserSummary | null>(null)
  const [editRole, setEditRole] = useState('')

  const { role: currentRole } = useAdminUser()
  const canCreate = usePermission('admin_users', 'create')
  const canEdit = usePermission('admin_users', 'edit')
  const canDelete = usePermission('admin_users', 'delete')

  const loadUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getAdminUsers({})
      if (response.ok && response.data) {
        setUsers(response.data.items)
        setTotal(response.data.total)
      } else {
        setError(response.error?.message || 'Failed to load admin users')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const handleCreate = async () => {
    if (!newEmail) return
    const response = await createAdminUser(newEmail, newRole)
    if (response.ok) {
      setShowCreate(false)
      setNewEmail('')
      setNewRole('support')
      loadUsers()
    } else {
      setError(response.error?.message || 'Failed to create admin user')
    }
  }

  const handleUpdate = async () => {
    if (!editingUser || !editRole) return
    const response = await updateAdminUser(editingUser.id, editRole)
    if (response.ok) {
      setEditingUser(null)
      setEditRole('')
      loadUsers()
    } else {
      setError(response.error?.message || 'Failed to update admin user')
    }
  }

  const handleRemove = async (id: string) => {
    if (!confirm('Are you sure you want to remove this admin user?')) return
    const response = await removeAdminUser(id)
    if (response.ok) {
      loadUsers()
    } else {
      setError(response.error?.message || 'Failed to remove admin user')
    }
  }

  // Filter role options based on current user's role
  const getAvailableRoles = () => {
    const currentRank = roleOptions.indexOf(currentRole || '')
    return roleOptions.slice(0, currentRank)
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-gray-100">Admin Users</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{total} admin users</p>
        </div>
        {canCreate && (
          <CustomButton onClick={() => setShowCreate(true)} variant="primary">
            Add Admin User
          </CustomButton>
        )}
      </div>

      {error && <div className="mb-6 rounded-xl bg-red-50 dark:bg-red-900/30 p-4 text-red-700 dark:text-red-300">{error}</div>}

      {/* Create Modal */}
      {showCreate && (
        <div className="mb-6 rounded-xl border border-gray-200 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] p-4">
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Add New Admin User</h3>
          <div className="space-y-4">
            <CustomTextInput
              label="Email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@example.com"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-4 py-2 text-sm"
              >
                {getAvailableRoles().map((role) => (
                  <option key={role} value={role}>{formatRole(role)}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <CustomButton onClick={handleCreate} variant="primary">Add User</CustomButton>
              <CustomButton onClick={() => setShowCreate(false)} variant="secondary">Cancel</CustomButton>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#503864] dark:border-[#7f4f9a] border-t-transparent" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">No admin users found</div>
      ) : (
        <div className="space-y-4">
          {users.map((user) => (
            <div key={user.id} className="rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#503864]/10 dark:bg-[#7f4f9a]/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-[#503864] dark:text-[#b58fcc]">
                      {(user.name || user.email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{user.name || user.email}</p>
                    {user.name && <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${roleColors[user.role]}`}>
                    {formatRole(user.role)}
                  </span>
                  {canEdit && editingUser?.id === user.id ? (
                    <div className="flex items-center gap-2">
                      <select
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                        className="rounded-lg border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-2 py-1 text-sm"
                      >
                        {getAvailableRoles().map((role) => (
                          <option key={role} value={role}>{formatRole(role)}</option>
                        ))}
                      </select>
                      <CustomButton onClick={handleUpdate} variant="primary">Save</CustomButton>
                      <CustomButton onClick={() => setEditingUser(null)} variant="secondary">Cancel</CustomButton>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      {canEdit && getAvailableRoles().includes(user.role) && (
                        <button
                          onClick={() => { setEditingUser(user); setEditRole(user.role); }}
                          className="text-sm text-[#503864] dark:text-[#b58fcc] hover:underline"
                        >
                          Edit
                        </button>
                      )}
                      {canDelete && getAvailableRoles().includes(user.role) && (
                        <button
                          onClick={() => handleRemove(user.id)}
                          className="text-sm text-red-600 dark:text-red-400 hover:underline"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
